import {
  ApiBonusStatusEnums,
  CashbackBonusType,
  DELAYED_RELOAD_BONUS_WHEN_EXPIRED,
  UserBonus,
  WageringConditionBonus,
} from 'config/types/bonus/userBonus'
import { useCountDown } from 'hooks/useCountdown'
import { usePrivateSocket } from 'hooks/usePrivateSocket'
import { useRequest } from 'hooks/useRequest'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import BonusService from 'services/BonusService'
import { useAppDispatch, useAppSelector } from 'state'
import { useAuth } from 'state/auth/hooks'
import { updateInPeriodCashbackBonus, updateUserBonuses } from 'state/bonus/actions'
import {
  useActivatedBonuses,
  useActiveBonusInWageringProcess,
  useActiveCashbackBonusReturnType,
} from 'state/bonus/hooks'
import { isCashbackBonusType } from 'utils/bonus'
import { minTwoDigits } from 'utils/numberHelper'
import { displayCountdownTimeUnit } from 'views/Cashback/utils'
import { useUserBonus } from './useUserBonus'

export const useFetchInPeriodCashbackBonus = () => {
  const dispatch = useAppDispatch()
  const { execute } = useRequest()
  const { isSigned } = useAuth()

  const handleFetchInPeriodCashbackBonus = useCallback(async () => {
    if (!isSigned) return
    const response = await execute(BonusService.getUserInPeriodCashbackBonus())
    if (response?.data) dispatch(updateInPeriodCashbackBonus({ bonus: response.data }))
    else dispatch(updateInPeriodCashbackBonus({ bonus: null }))
  }, [isSigned])

  return handleFetchInPeriodCashbackBonus
}

export const useBonusCountdownCallback = () => {
  const { fetchBonuses } = useUserBonus()
  const { activatedBonuses } = useActivatedBonuses()
  const fetchInPeriodCashbackBonus = useFetchInPeriodCashbackBonus()

  const callbackTasks = () => {
    fetchBonuses()
    fetchInPeriodCashbackBonus()
  }

  useEffect(() => {
    if (!activatedBonuses.length) return
    const timeouts = activatedBonuses
      .map((item) => {
        const maxTimeSetTimeOutAllowance = 2147483647
        let timeLeft = item.condition.expiryDate - Date.now() + DELAYED_RELOAD_BONUS_WHEN_EXPIRED
        if (timeLeft < 0) return null
        if (timeLeft > maxTimeSetTimeOutAllowance) timeLeft = maxTimeSetTimeOutAllowance

        return setTimeout(() => {
          callbackTasks()
        }, timeLeft)
      })
      .filter((item) => item)

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout))
    }
  }, [activatedBonuses])
}

export const useCashbackBonusUpdater = () => {
  const { activeBonus, activeBonusInCashbackDuration } = useCashbackBonus()
  const socket = usePrivateSocket()
  const dispatch = useAppDispatch()
  const { bonuses: activeBonuses } = useUserBonus()
  const { isSigned } = useAuth()
  const fetchInPeriodCashbackBonus = useFetchInPeriodCashbackBonus()

  useEffect(() => {
    fetchInPeriodCashbackBonus()
  }, [isSigned])

  useEffect(() => {
    if (activeBonus && activeBonusInCashbackDuration) {
      dispatch(updateInPeriodCashbackBonus({ bonus: activeBonus }))
    }
  }, [activeBonus, activeBonusInCashbackDuration])

  useEffect(() => {
    if (socket) {
      const CashbackProfitTrackingEvent = socket.on('voucher.cashback.profit.tracking', (data) => {
        dispatch(
          updateUserBonuses({
            userBonuses:
              activeBonuses?.map((item) => {
                if (item.id === data.voucher_user_id) {
                  ;(item as CashbackBonusType).updateStatistics({
                    cashbackRate: Number(data.cashback_rate) * 100,
                    payoutAmountInUsd: data.total_payout_usd,
                    wagerAmountInUsd: data.total_wager_usd,
                  })
                  return item
                }

                return item
              }) || [],
          }),
        )
      })

      return () => {
        CashbackProfitTrackingEvent.off()
      }
    }
  }, [socket, activeBonuses])
}

export const useCashbackBonus = (): useActiveCashbackBonusReturnType => {
  const bonus = useActiveBonusInWageringProcess()
  const inPeriodCashbackBonus = useAppSelector((state) => state.bonus.inPeriodCashbackBonus)

  const activeBonus: CashbackBonusType | null = useMemo(() => (isCashbackBonusType(bonus) ? bonus : null), [bonus])
  const { t } = useTranslation()

  const bonusCountdownRef = useCountDown(
    activeBonus?.condition?.expiryDate || Date.now(),
    (s, m, h, d) => `
      ${displayCountdownTimeUnit(t('day'), 'day', minTwoDigits(d))}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('hour'), 'hour', minTwoDigits(h))}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('min'), 'min', minTwoDigits(m))}
      <div class='colon-mark'>:</div>
      ${displayCountdownTimeUnit(t('sec'), 'sec', minTwoDigits(s))}
          `,
    () => {},
  )

  return useMemo(
    () => ({
      activeBonusInCashbackDuration:
        (activeBonus && activeBonus.apiStatus === ApiBonusStatusEnums.BetCollecting) || false,
      activeBonus: activeBonus || null,
      inPeriodCashbackBonus,
      bonusCountdownRef,
      isCancelledBonus: (inPeriodCashbackBonus as unknown as UserBonus)?.IsCancelled,
      isExpiredBonus: inPeriodCashbackBonus?.condition?.isExpired,
    }),
    [activeBonus, bonusCountdownRef, inPeriodCashbackBonus, bonus],
  )
}

export const useInvalidActiveCashbackBonus = (bonus: UserBonus) => {
  const { activeBonus: activeCashbackBonus } = useCashbackBonus()
  const activeWageringBonus = useActiveBonusInWageringProcess()

  const preventedByActiveCashbackBonus =
    !isCashbackBonusType(bonus) && bonus instanceof WageringConditionBonus && activeCashbackBonus

  const cashbackBonusPreventedByActiveWageringBonus =
    isCashbackBonusType(bonus) &&
    !isCashbackBonusType(activeWageringBonus) &&
    activeWageringBonus instanceof WageringConditionBonus

  const cashbackBonusPreventedByActiveCashbackBonus =
    isCashbackBonusType(bonus) && activeCashbackBonus && activeCashbackBonus?.id !== bonus?.id

  return {
    preventedByActiveCashbackBonus,
    cashbackBonusPreventedByActiveWageringBonus,
    cashbackBonusPreventedByActiveCashbackBonus,
  }
}
